import { useState, useContext, useEffect, useRef, memo, useImperativeHandle } from "react";
import { useReactFlow, getIncomers, getOutgoers } from "reactflow";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import { toast } from "react-toastify";
import { encode } from "api/UtilityServiceApis";
import { removeDuplicateObjects, deepEqual, isFunction, tryCatchWrapper } from "helpers/common";
import sortString from "helpers/SortString";
import { hideComponentPropsPanel, setComponentPropsPanelFormIsDirty } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import { updateFlowSteps } from "redux/actions/flowDesigner/flowsActionCreator";
import { updateContextVariable } from "redux/actions/flowDesigner/editorContextActionCreator";
import { restApiFields, payloadContentTypeFields } from "layouts/Flow Designer/data/restApiFields";
import { sftpFields } from "layouts/Flow Designer/data/sftpFields";
import { TabContextApi, ComponentPropsContext, ComponentPropsContextApi } from "../../context";
import { COMMON_COMPONENT_TYPES, CONDITION_OPERATORS, NODE_TYPES, ACTION_TYPES, CANVAS_DIRECTIONS, REQUIRED_CREDENTIAL_COMPONENTS } from "constants/flowDesigner";
import "../../css/configItems.css";
import { useHandleFloatingEdge } from "hooks/useHandleFloatingEdge";
import { replaceStringContextValue } from "../../../../helpers/replaceStringContextValue";
import { SET_CURRENT_ACTION_FORM } from "redux/constants/flowDesigner";
import { findValueFromContext } from "helpers/common";
import { useDetermineShareFlowUserAccessType } from "hooks/useDetermineShareFlowUserAccessType";
import useAllowEditWorkflow from "hooks/useAllowEditWorkflow";
import { FormBody } from "./components/FormBody";
import { getEnv } from "helpers/environment";
import { useFlexLayout } from "context/AppProvider/FlexLayoutContext";
import { useTheme } from "@mui/material/styles";
import { THEME_COLORS } from "constants/theme";
import { isVariableValueEmpty, normalizeContextPath } from "../../helpers/common";
import { updateComponentPropsTab } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import { Actions } from "flexlayout-react";
import { ReactComponent as FlowDefaultIcon } from "assets/images/for-flow/flow-icon-list-view.svg";
import Radio from "@mui/material/Radio";
import useHandleStraightEdge from "hooks/useHandleStraightEdge";
import useCreateSubFlowNode from "hooks/useCreateSubFlowNode";
import useAutoLayout from "hooks/useAutoLayout";
import {
   createResourceDependency,
   deleteResourceDependency,
   updateResourceDependency
} from "redux/actions/portal/resourceDependenciesActionCreator";
import { getResourceDependencyContextId } from "helpers/resourceDependencies";

const CONTEXT_REFERENCE_REGEX = /^@(?:(?:(?:variables|steps|triggers|flow_configuration|shared_flow_configurations|invocation|integration_ids|settings|session)(?:\.[a-zA-Z0-9_-]+|\[\d+\])+)|flow_id|flow_name|tenant_id|session)$/;
const isContextReference = (value) => typeof value === "string" && CONTEXT_REFERENCE_REGEX.test(value);

export const styles = {
   w100: {
      width: "100%"
   },
   w50: {
      width: "50%"
   },
   w49: {
      width: "49%"
   },
   w39: {
      width: "39%"
   },
   height100: {
      height: "100%"
   },
   relativeWidth: {
      width: "calc(100% - 30px)"
   },
   listItemMargin_t16: {
      marginTop: "16px"
   },
   listItemMargin_t42: {
      marginTop: "42px"
   },
   gap: {
      gap: 10
   },
   asterisk: {
      color: "rgb(255, 102, 124);"
   }
};

const ActionsWrapper = styled(Box)(() => ({
   height: "100%",
   display: "flex",
   flexDirection: "column",
   minHeight: 0
}));

export const noInputOperators = [CONDITION_OPERATORS.IS_EMPTY, CONDITION_OPERATORS.IS_NOT_EMPTY, CONDITION_OPERATORS.IS_TRUE, CONDITION_OPERATORS.IS_FALSE, CONDITION_OPERATORS.IS_NULL, CONDITION_OPERATORS.IS_NOT_NULL];

const ComponentActionsGenerator = ({ tabId, ref, tabState, onDirtyChange }) => {
   const { flow, currentSavedFlow, flows } = useSelector((store) => store?.designer?.flows);
   const { selectedNode, flowElement, updateFlowElementCallback, showComponentPropsPanel, isDirty, currentActionForm } = tabState;
   const dispatch = useDispatch();
   const [showMainList, setShowMainList] = useState(true);
   const [componentAction, setComponentAction] = useState(null);
   const { t } = useTranslation("flowDesigner");
   const [actionForm, setActionForm] = useState(null);
   const [initialActionForm, setInitialActionForm] = useState(null);
   const [variableWithJson, setVariableWithJson] = useState(null);
   const [formErrors, setFormErrors] = useState([]);
   const reactFlowInstance = useReactFlow();
   const { getNode, getNodes, getEdges, setNodes, getEdge, setEdges } = reactFlowInstance;
   const { autoSaveFlow } = useContext(TabContextApi);
   const { actions, type } = selectedNode ?? {};
   const isCommonComponent = type === NODE_TYPES.COMMON || type === NODE_TYPES.LABEL;
   const isMultipleActions = actions?.length > 1;
   const codeEditorInputRef = useRef(null);
   const sqlEditorInputRef = useRef(null);
   const actionFieldsRef = useRef(null);
   const { addFloatingEdge } = useHandleFloatingEdge();
   const isFlowActive = currentSavedFlow?.shared_status === "active";
   const { isReadOnlyAccess } = useDetermineShareFlowUserAccessType(currentSavedFlow?.id);
   const { allowEditWorkflow } = useAllowEditWorkflow();
   const { context } = useSelector((store) => store?.designer?.editorContext);
   const { model } = useFlexLayout();
   const theme = useTheme();
   const isDarkTheme = theme.palette.mode === "dark";
   const [shouldManuallySave, setShouldManuallySave] = useState(false);
   const { addStraightEdge } = useHandleStraightEdge();
   const { createSubFlowNode } = useCreateSubFlowNode();
   const { isVerticalCanvasDirection } = useSelector((store) => store?.designer?.settings);
   const { updateLayout } = useAutoLayout({
      direction: isVerticalCanvasDirection ? CANVAS_DIRECTIONS.VERTICAL : CANVAS_DIRECTIONS.HORIZONTAL,
      manualRun: true
   });
   const hasMounted = useRef(false);
   const selectionRef = useRef(null);

   const filterValidationErrors = (field) => {
      setFormErrors((prev) => prev?.filter((error) => error !== field));
   };

   const setCredentialActions = (action) => {
       let targetAction = action || actions[0];
       const isCredentialRequired = REQUIRED_CREDENTIAL_COMPONENTS.has(selectedNode.key);
       const newFields = [
         {
            field: "credential",
            label: "Select Credential",
            type: "credential",
            isDisabled: isFlowActive,
            isRequired: isCredentialRequired || !!selectedNode?.connectorId
         },
          isCredentialRequired ? {
             field: "credentialRequired",
             type: "hidden",
             value: true
          } : {
             field: "credentialRequired",
             label: "Requires Credential",
             type: ACTION_TYPES.CREDENTIAL_SWITCH,
             isDisabled: isFlowActive
          }
       ];

       targetAction.componentType = selectedNode.key;

       const actionFields = targetAction?.dataInsideAction ?? [];
       const authenticationTypeIndex = actionFields.findIndex((field) => field.field === "authenticationType");

       // MCP chooses its authentication method first. Keep the legacy bearer
       // picker immediately below that choice instead of at the top of the form.
       targetAction.dataInsideAction = selectedNode.key === COMMON_COMPONENT_TYPES.MCP_CLIENT && authenticationTypeIndex !== -1
          ? [...actionFields.slice(0, authenticationTypeIndex + 1), ...newFields, ...actionFields.slice(authenticationTypeIndex + 1)]
          : [...newFields, ...actionFields];

      return targetAction;
   };

   const setErrorHandlingAction = (action) => {
      let targetAction = action || actions[0];

      // if errorHandling field is not present, add it
      if (!targetAction.dataInsideAction.find((d) => d.field === "errorHandling")) {
         const newField = {
            field: "errorHandling",
            label: "Error Handling",
            type: ACTION_TYPES.ERROR_HANDLING_DROPDOWN,
            value: "stop",
            options: [
               {
                  value: "continue",
                  text: "Continue on error"
               },
               {
                  value: "stop",
                  text: "Stop on error"
               }
            ],
            disabled: isFlowActive
         };

         targetAction.dataInsideAction = [...(targetAction?.dataInsideAction ?? []), newField];
      }
      return targetAction;
   };

   const setInvokeComponentActions = (action) => {
      let targetAction = action || actions[0];
      let flowElementData = flowElement.data || {};

      if (currentSavedFlow) {
         let invokeFlowOptions = getInvokeFlowOptions({
            isInvokeSubFlowOptions: true
         });
         if (invokeFlowOptions.length) {
            invokeFlowOptions = invokeFlowOptions.sort((a, b) => a.text.localeCompare(b.text)).map((o) => ({ ...o, label: o.text }));
         }
         targetAction.dataInsideAction = targetAction.dataInsideAction?.map((action) => {
            if (action.field === "flowId") {
               return {
                  ...action,
                  options: invokeFlowOptions
               };
            }

            return action;
         });
      }

      const newDataInsideAction = targetAction.dataInsideAction.map((action) => {
         if (action.field === "parameters") {
            const newListItems = [action.listItems[0]];
            for (const key in flowElementData) {
               if (key.startsWith("var_")) {
                  const varNumber = key.split("var_")[1];
                  const savedParameter = flowElementData[key] || {};
                  newListItems.push({
                     field: `var_${varNumber}`,
                     label: "Name",
                     type: "string",
                     dataType: savedParameter.dataType,
                     placeholder: "",
                     value: "",
                     secondaryInput: {
                        field: `value_${varNumber}`,
                        value: savedParameter.secondaryInput?.value ?? "",
                        label: "Value",
                        placeholder: "",
                        isRequired: false
                     },
                     defaultInput: {
                        field: `defVal_${varNumber}`,
                        value: savedParameter?.defaultInput?.value ?? "",
                        label: "Default value",
                        placeholder: "",
                        isRequired: false
                     },
                     isRequired: true
                  });
               }
            }
            newListItems.push({
               field: "addParameter",
               type: "button",
               label: "Add Parameter"
            });

            return {
               ...action,
               listItems: newListItems
            };
         }
         return action;
      });

      targetAction.dataInsideAction = newDataInsideAction;

      return targetAction;
   };

   const setSessionStorageActions = (action) => {
      const targetAction = action || actions[0];

      const currentActionValue = actionForm?.action?.value || flowElement?.data?.action?.value;

      // 1. Base fields — always shown
      const baseFields = [
         {
            field: "action",
            label: "Action",
            type: "dropdown",
            isRequired: true,
            options: [
               { value: "check", text: "Check" },
               { value: "createIfNew", text: "Create If New" },
               { value: "retrieve", text: "Retrieve" },
               { value: "update", text: "Update" },
               { value: "delete", text: "Delete" }
            ]
         },
         {
            field: "sessionId",
            label: "Session ID",
            type: "text",
            placeholder: "e.g. {{msg.sessionId}}",
            isRequired: true
         }
      ];

      if (["createIfNew", "update"].includes(currentActionValue)) {
         baseFields.push({
            field: "value",
            label: "Value (Session Object)",
            type: "text",
            isRequired: currentActionValue === "update",
            defaultValue: currentActionValue === "update" ? "session" : "{}",
            placeholder: currentActionValue === "update" ? "Required: The variable to write back" : "Optional: Initial JSON object"
         });
      }

      if (currentActionValue === "createIfNew") {
         baseFields.push({
            field: "ttl",
            label: "TTL (minutes)",
            type: "text",
            isRequired: false,
            placeholder: "Optional — default is 30 days"
         });
      }

      if (currentActionValue === "update") {
         baseFields.push({
            field: "merge",
            label: "Merge",
            type: "checkbox",
            value: actionForm?.merge?.value ?? true
         });
      }

      targetAction.dataInsideAction = baseFields;
      return targetAction;
   };

   const getUpdatedActions = (action) => {
      try {
         const flowElementData = flowElement?.flowElement?.data || flowElement?.data;
         const hasEnteredData = !!flowElementData;
         let cloneAction = Object.assign({}, action);

         if (cloneAction.isCredentialSupported) {
            cloneAction = setCredentialActions(cloneAction);
         }

          switch (selectedNode.key) {
             case COMMON_COMPONENT_TYPES.VARIABLES:
                return tryCatchWrapper(setVariableActions)(cloneAction);
             case COMMON_COMPONENT_TYPES.REDIS:
                return tryCatchWrapper(setStringActions)(cloneAction);
             case COMMON_COMPONENT_TYPES.REST_API:
             case COMMON_COMPONENT_TYPES.WEBHOOK:
                return tryCatchWrapper(setRestAndWebhookComponentActions)(cloneAction);
             case COMMON_COMPONENT_TYPES.SFTP:
                return tryCatchWrapper(setSftpComponentActions)(cloneAction);
             case COMMON_COMPONENT_TYPES.GOTO:
                return tryCatchWrapper(setGoToComponentActions)(hasEnteredData ? cloneAction : null);
             case COMMON_COMPONENT_TYPES.INVOKE:
                return tryCatchWrapper(setInvokeComponentActions)(hasEnteredData ? cloneAction : null);
            // ── SESSION STORAGE ──────────────────────────────────────────
             case COMMON_COMPONENT_TYPES.SESSION_STORAGE:
                return tryCatchWrapper(setSessionStorageActions)(cloneAction);
            // ─────────────────────────────────────────────────────────────
             default:
                return cloneAction;
          }
      } catch (error) {
         console.error("error", error);
      }
   };

   const onListItemClick = (action) => {
      let cloneAction = getUpdatedActions(action);

      cloneAction = setErrorHandlingAction(cloneAction);

      const form = getFormFields(cloneAction.dataInsideAction);

      setFormErrors([]);
      setShowMainList(false);
      setInitialandActualActionForm(form);
      setComponentAction(cloneAction);
      presetFormValues(form);
   };

   const getFormFields = (dataInsideAction = []) => {
      let form = {};

      for (const item of dataInsideAction) {
         if (item.field) {
            if (item.allowVariableSelection) {
               form.inputType = "manual";
               form[item.field] = {
                  value: item.value || "",
                  isPassword: false
               };
            } else if (item.type === ACTION_TYPES.LIST) {
               item.listItems?.forEach((li) => {
                  if (li.type === ACTION_TYPES.KEY_VALUE) {
                     form[li.field] = {
                        value: li.value || [],
                        isPassword: false
                     };
                  } else {
                     form[li.field] = {
                        value: li.value || "",
                        isPassword: false
                     };
                  }
               });
            } else {
               const defaultValue = generateDefaultLabel(item);

               form[item.field] = {
                  value: item.value || defaultValue,
                  isPassword: item.type === ACTION_TYPES.PASSWORD
               };
            }
         }
      }

      return form;
   };

   const generateDefaultLabel = (item) => {
      const nodeKey = selectedNode.key;
      const isLabel = nodeKey === NODE_TYPES.LABEL;

      if (isLabel && item.field === "labelName") {
         return getNode(selectedNode.elementId)?.data?.label;
      }

      if (item.field === "operator" && (nodeKey === COMMON_COMPONENT_TYPES.CONDITIONAL || nodeKey === COMMON_COMPONENT_TYPES.SELECTION)) {
         return CONDITION_OPERATORS.EQUAL;
      }

      return "";
   };

   const presetFormValues = (defaults = {}) => {
      const flowElementData = flowElement?.flowElement?.data || flowElement?.data;
      if (!flowElementData) return;

      if (selectedNode.key === COMMON_COMPONENT_TYPES.EXECUTION_REPORT) {
         setExecutionReportActions();
      } else if (selectedNode.type === NODE_TYPES.CUSTOM) {
         removeEmptyValues(flowElementData);
      }
      setInitialandActualActionForm(selectedNode.connectorId ? { ...defaults, ...flowElementData } : flowElementData);
   };

   const cloneFormState = (data) => {
      if(data === null || data === undefined) return data;
      return JSON.parse(JSON.stringify(data));
   }

   const setInitialandActualActionForm = (data) => {
      const normalizedData = REQUIRED_CREDENTIAL_COMPONENTS.has(selectedNode.key)
         ? {
            ...data,
            credentialRequired: {
               ...data?.credentialRequired,
               value: true
            }
         }
         : data;

      setActionForm(cloneFormState(normalizedData));
      setInitialActionForm(cloneFormState(normalizedData));
   };

   const generateActions = () => {
      if (!actions) {
         return null;
      }
      return actions.map((action, index) => {
         const selectedAction = action.actionName === flowElement?.action;

         return (
            <ListItem key={action.actionID} divider={index < actions.length - 1}>
               <ListItemButton onClick={() => tryCatchWrapper(onListItemClick)(action)}>
                  <Radio checked={selectedAction} />
                  <ListItemText className={`node-action-name ${selectedAction && "selected-action"}`} primary={action.actionName} />
               </ListItemButton>
            </ListItem>
         );
      });
   };

    const checkFormErrors = () => {
       let errors = [];

       const isActionVisible = (action) => {
          if (action.hideUntilOAuthAuthenticated) {
             const isOAuth = actionForm.authenticationType?.value === "oauth";
             if (isOAuth && !actionForm.oauthConnectionId?.value) return false;
          }

          const visibleWhen = action.visibleWhen;
          return !visibleWhen || actionForm[visibleWhen.field]?.value === visibleWhen.value;
       };

       componentAction.dataInsideAction?.forEach((action) => {
          if (!isActionVisible(action)) return;

          if (action.type === "list") {
             action.listItems.forEach((li) => {
                if (li.isRequired && (!actionForm[li.field]?.value || !actionForm[li.field]?.value.length)) {
                   errors.push(li.field);
                }
             });
          } else if (action.type === ACTION_TYPES.CREDENTIAL_SWITCH) {
            if (actionForm["credentialRequired"]?.value && !actionForm["credential"]?.value) {
               errors.push("credential");
            }
         } else if (action.type === ACTION_TYPES.FILE && Array.isArray(actionForm[action.field]?.value) && actionForm[action.field]?.value?.reduce((accu, curr) => accu + curr.size, 0) > getEnv("MAX_FILE_COMBINED_SIZE")) {
            errors.push("attachments");
          } else {
            if (action.isRequired && (!actionForm[action.field]?.value || !actionForm[action.field]?.value?.length)) {
                errors.push(action.field);
             }
          }
       });

       if (selectedNode.key === COMMON_COMPONENT_TYPES.MCP_CLIENT && actionForm.authenticationType?.value === "oauth") {
          errors.push("authenticationType");
          toast.error("OAuth is currently unavailable. Select None or Bearer token for MCP authentication.");
       }

       if ([COMMON_COMPONENT_TYPES.VARIABLES, COMMON_COMPONENT_TYPES.INVOKE].includes(selectedNode.key)) {
            const nameFields = Object.entries(actionForm)
               .filter(([field]) => field.startsWith("var_") || field.startsWith("reference_var_"))
               .map(([field, variable]) => ({ field, name: typeof variable?.value === "string" ? variable.value.trim() : "" }));

            const filteredNames = nameFields.filter(({ field, name }) => {
               if (!name) {
                  errors.push(field);
                  return false;
               }
               return true;
            });
            const duplicateNames = filteredNames.map(({ name }) => name).filter((name, index, allNames) => allNames.indexOf(name) !== index);
            errors.push(...filteredNames.filter(({ name }) => duplicateNames.includes(name)).map(({ field }) => `duplicate_variable_name_${field}`));

            Object.entries(actionForm)
               .filter(([field, variable]) => field.startsWith("var_") && isTypedVariableValueInvalid(variable, variable?.secondaryInput?.value))
               .forEach(([field]) => errors.push(`invalid_variable_value_${field}`));

            Object.entries(actionForm)
               .filter(([field, variable]) => field.startsWith("var_") && isTypedDefaultValueInvalid(variable))
               .forEach(([field]) => errors.push(`invalid_variable_default_${field}`));
       }

      return [...new Set(errors)];
   };

   const submitActions = async () => {
      let errors = checkFormErrors();

      if (errors.length) {
         setFormErrors(errors);
         return;
      }

      const { key, elementId, type } = selectedNode;
      const { actionName, variables } = componentAction;
      let stepNo = flow.steps?.length + 1;

      if (key === COMMON_COMPONENT_TYPES.EXECUTION_REPORT) {
         const selected = checkExecutionReportOptionSelection();
         if (!selected) {
            return;
         }
      }

      await encodePasswords();

      if (key !== COMMON_COMPONENT_TYPES.SELECTION && type === NODE_TYPES.COMMON) {
         removeEmptyValues(actionForm);
      }

      if (key === COMMON_COMPONENT_TYPES.VARIABLES) {
         checkAndUpdateFlowVariables();
      }

      if (key === COMMON_COMPONENT_TYPES.CONDITIONAL && noInputOperators.includes(actionForm.operator?.value)) {
         if (!actionForm.valueToCompareWith) {
            actionForm.valueToCompareWith = {};
         }
         actionForm.valueToCompareWith.value = "";
      }

      if (key === COMMON_COMPONENT_TYPES.SELECTION) {
         // update actionform branches with the updated value
         actionForm.branches = actionForm.branches.map((branch, index) => {
            if (index === 0) {
               return {
                  ...branch,
                  isDefaultEnabled: branch.isDefaultEnabled ?? true
               };
            }

            if (branch.value) {
               const node = getNode(branch.id);
               if (node) {
                  branch.key = `${branch.startPoint}-${branch.value.toLowerCase()}`;
                  // branch.label = branch.value;
                  branch.name = branch.value;
               }
            }
            return branch;
         });
      }

      const element = {
         stepNumber: stepNo,
         component: key,
         elementId: elementId,
         action: actionName,
         variables: variables,
         remarks: "",
         data: actionForm,
         nextStep: ++stepNo
      };

      if (componentAction.isCredentialSupported) {
         element.isCredentialSupported = true;
      }

      if (key === COMMON_COMPONENT_TYPES.GOTO) {
         if (actionForm.nodeId.value) {
            addFloatingEdge(selectedNode.elementId, actionForm.nodeId.value);
         }
      }
       let flowNodes = getNodes();

        if (key === COMMON_COMPONENT_TYPES.VARIABLES) {
           const syncedFlowNodes = syncVariableDeclarationReferences(flowNodes, element);
           if (!syncedFlowNodes) return;
           flowNodes = syncedFlowNodes;
        }

      if (key === COMMON_COMPONENT_TYPES.INVOKE) {
         // add static node beside the invoke node
         // add edge from invoke node to the static node
         const currentNode = getNode(selectedNode.elementId);
         const subFlowId = actionForm.flowId?.value;
         const subFlowName = flows.find((f) => f.id === subFlowId)?.name;
         const runSynchronously = actionForm.runSynchronously?.value ?? true;

         const subFlowNode = flowNodes.find((n) => n.type === NODE_TYPES.SUBFLOW && n.data?.parentNodeId === currentNode.id);
         if (currentNode && subFlowId) {
            if (subFlowNode) {
               flowNodes = flowNodes.map((n) => {
                  if (n.id !== subFlowNode.id) return n;

                  const edgeId = `${currentNode.id}->straight->${n.id}`;
                  const edge = getEdge(edgeId);
                  if (edge) {
                     const updatedEdge = {
                        ...edge,
                        style: {
                           ...edge.style,
                           strokeDasharray: runSynchronously ? "0" : "5,5"
                        }
                     };

                     setEdges((eds) => eds.map((e) => (e.id === edge.id ? updatedEdge : e)));
                  }

                  return {
                     ...n,
                     data: {
                        ...n.data,
                        flowId: subFlowId,
                        label: subFlowName
                     }
                  };
               });
            } else {
               const nodeData = createSubFlowNode(currentNode, {
                  id: subFlowId,
                  name: subFlowName
               });

               addStraightEdge(currentNode.id, nodeData.id, runSynchronously);

               flowNodes = [...flowNodes, nodeData];
            }
         }
      }

      if (selectedNode.key === COMMON_COMPONENT_TYPES.SELECTION) {
         actionForm?.branches?.forEach((branch) => {
            if (!branch.value) return;

            flowNodes = flowNodes.map((node) => {
               if (node.id !== branch.id) return node;

               if (branch.isVariable) {
                  const path = normalizeContextPath(branch.value);
                  branch.value = findValueFromContext(context, path);
               }

               return node;
            });
         });
      }

       if (key === NODE_TYPES.LABEL && actionForm.labelName?.value?.trim()) {
         updateCanvasLabelNode(element, flowNodes);
      }

      const previousCredentialId = initialActionForm?.credential?.value;
      const credentialId = actionForm.credential?.value;
      const dependencySourceId = selectedNode.connectorId || currentSavedFlow?.id;
      const dependencySourceType = selectedNode.connectorId ? "connector" : "flow";
      const dependencyContextId = getResourceDependencyContextId(
         currentSavedFlow?.id,
         selectedNode.elementId
      );
      const hasOldDepToDelete = previousCredentialId && previousCredentialId !== credentialId;

      if (hasOldDepToDelete && credentialId) {
         dispatch(
            updateResourceDependency({
               tenantId: currentSavedFlow.tenant_id,
               oldInput: {
                  sourceId: dependencySourceId,
                  targetId: previousCredentialId,
                  sourceResourceType: dependencySourceType,
                  targetResourceType: "credential",
                  contextId: dependencyContextId
               },
               newInput: {
                  sourceId: dependencySourceId,
                  targetId: credentialId,
                  sourceResourceType: dependencySourceType,
                  targetResourceType: "credential",
                  contextId: dependencyContextId
               }
            })
         );
      } else if (credentialId && previousCredentialId !== credentialId) {
         dispatch(
            createResourceDependency({
               tenantId: currentSavedFlow.tenant_id,
               sourceId: dependencySourceId,
               targetId: credentialId,
               sourceResourceType: dependencySourceType,
               targetResourceType: "credential",
               contextId: dependencyContextId
            })
         );
      } else if (hasOldDepToDelete) {
         dispatch(
            deleteResourceDependency({
               tenantId: currentSavedFlow.tenant_id,
               sourceId: dependencySourceId,
               targetId: previousCredentialId,
               sourceResourceType: dependencySourceType,
               targetResourceType: "credential",
               contextId: dependencyContextId
            })
         );
      }

      const previousSubFlowId = initialActionForm?.flowId?.value;
      const subFlowId = actionForm.flowId?.value;
      const subFlowContextId = getResourceDependencyContextId(
         currentSavedFlow?.id,
         selectedNode.elementId
      );
      const hasOldSubFlowToDelete = previousSubFlowId && previousSubFlowId !== subFlowId;
      const hasNewSubFlowToCreate = selectedNode.key === COMMON_COMPONENT_TYPES.INVOKE && subFlowId;

      if (hasOldSubFlowToDelete && hasNewSubFlowToCreate) {
         dispatch(
            updateResourceDependency({
               tenantId: currentSavedFlow.tenant_id,
               oldInput: {
                  sourceId: currentSavedFlow.id,
                  targetId: previousSubFlowId,
                  sourceResourceType: "flow",
                  targetResourceType: "flow",
                  contextId: subFlowContextId
               },
               newInput: {
                  sourceId: currentSavedFlow.id,
                  targetId: subFlowId,
                  sourceResourceType: "flow",
                  targetResourceType: "flow",
                  contextId: subFlowContextId
               }
            })
         );
      } else if (hasNewSubFlowToCreate && previousSubFlowId !== subFlowId) {
         dispatch(
            createResourceDependency({
               tenantId: currentSavedFlow.tenant_id,
               sourceId: currentSavedFlow.id,
               targetId: subFlowId,
               sourceResourceType: "flow",
               targetResourceType: "flow",
               contextId: subFlowContextId
            })
         );
      } else if (hasOldSubFlowToDelete) {
         dispatch(
            deleteResourceDependency({
               tenantId: currentSavedFlow.tenant_id,
               sourceId: currentSavedFlow.id,
               targetId: previousSubFlowId,
               sourceResourceType: "flow",
               targetResourceType: "flow",
               contextId: subFlowContextId
            })
         );
      }

       setInitialActionForm(cloneFormState(element.data));
       onDirtyChange?.(false);
       dispatch(updateComponentPropsTab(tabId, element));
       setNodes(flowNodes.map((node) => ({ ...node, selected: false })));
       updateFlowElementCallback(element);
       dispatch(setComponentPropsPanelFormIsDirty(tabId, false));
      selectionRef.current?.resetToDefault?.();
      setTimeout(() => {
         try {
            updateStepDataContext(element);
         } catch (error) {
            console.error("error updating step data context", error);
         }
         updateLayout();
         dispatch(updateFlowSteps(element));
         autoSaveFlow(reactFlowInstance);
         setFormErrors([]);
      }, 10);
   };

    const syncVariableDeclarationReferences = (nodes, updatedElement) => {
       const variableNode = getNode(selectedNode.elementId);
       const componentKey = variableNode?.data?.componentKey;
       if (!componentKey) return nodes;

        const previousDeclarations = Object.entries(variableNode.data?.flowElement?.data ?? {})
           .filter(([field, name]) => (field.startsWith("var_") || field.startsWith("reference_var_")) && name?.value)
           .map(([field, name]) => ({ field, name: name.value.trim() }));
        const updatedDeclarationNames = new Map(
           Object.entries(updatedElement.data ?? {})
              .filter(([field, name]) => (field.startsWith("var_") || field.startsWith("reference_var_")) && name?.value)
              .map(([field, name]) => [field, name.value.trim()])
        );
         const previousReferences = previousDeclarations.filter(({ field }) => field.startsWith("reference_var_"));
         const updatedReferenceNames = new Map(
            [...updatedDeclarationNames].filter(([field]) => field.startsWith("reference_var_"))
         );
         const savedReferenceNames = new Set(updatedReferenceNames.values());
         const referencePrefix = `variables.${componentKey}.`;
          const renamedReferencePaths = previousDeclarations
             .map(({ field, name }) => ({ from: `${referencePrefix}${name}`, to: updatedDeclarationNames.get(field) && `${referencePrefix}${updatedDeclarationNames.get(field)}` }))
             .filter(({ from, to }) => to && from !== to);
         const replacementByPath = new Map(renamedReferencePaths.map(({ from, to }) => [from, to]));
         const referencePathPattern = renamedReferencePaths.length
            ? new RegExp(`(^|[^A-Za-z0-9_$.])(${renamedReferencePaths.map(({ from }) => from.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})(?![A-Za-z0-9_$])`, "g")
            : null;

         const replaceReferencePaths = (value) => {
            if (!referencePathPattern || !value.includes(referencePrefix)) return value;

            return value.replace(referencePathPattern, (_, prefix, referencePath) => `${prefix}${replacementByPath.get(referencePath)}`);
         };

        const replaceReferencesInData = (value) => {
            if (typeof value === "string") return value.includes(referencePrefix) ? replaceReferencePaths(value) : value;
           if (Array.isArray(value)) {
              const updatedItems = value.map(replaceReferencesInData);
              return updatedItems.some((item, index) => item !== value[index]) ? updatedItems : value;
           }
           if (!value || typeof value !== "object") return value;

           let hasChanges = false;
           const updatedEntries = Object.entries(value).map(([field, fieldValue]) => {
              const updatedValue = replaceReferencesInData(fieldValue);
              hasChanges ||= updatedValue !== fieldValue;
              return [field, updatedValue];
           });

           return hasChanges ? Object.fromEntries(updatedEntries) : value;
         };

         return nodes.map((node) => {
            const flowElementData = node.data?.flowElement?.data;
            const updatedFlowElementData = replaceReferencesInData(flowElementData);
            const updatedNodeData = updatedFlowElementData === flowElementData
               ? node.data
               : { ...node.data, flowElement: { ...node.data.flowElement, data: updatedFlowElementData } };
            const reference = node.data?.variable_reference;
           if (!reference?.startsWith(referencePrefix)) {
              return updatedNodeData === node.data ? node : { ...node, data: updatedNodeData };
           }

            const referenceName = reference.slice(referencePrefix.length);
            const previousReference = previousReferences.find(({ name }) => name === referenceName);
            const nextReferenceName = updatedReferenceNames.get(previousReference?.field);
            if (nextReferenceName && nextReferenceName !== referenceName) {
               return {
                  ...node,
                  data: { ...updatedNodeData, variable_reference: `${referencePrefix}${nextReferenceName}` }
               };
            }
            if (nextReferenceName || savedReferenceNames.has(referenceName)) return updatedNodeData === node.data ? node : { ...node, data: updatedNodeData };

           const nodeData = { ...updatedNodeData };
           delete nodeData.variable_reference;
           return { ...node, data: nodeData };
        });
    };

   const getContextValue = (fieldValue, context) => {
      if (!fieldValue || typeof fieldValue !== "string") return undefined;

      if (!fieldValue.startsWith("@")) return fieldValue;

      const path = normalizeContextPath(fieldValue);

      return path.split(".").reduce((obj, key) => {
         if (obj && obj[key] !== undefined) {
            return obj[key];
         }
         return undefined;
      }, context);
   };

   const getVariableDefaultValue = (variable) => variable?.defaultInput?.value;

   const isTypedVariableValueInvalid = (variable, value) => {
      const dataType = variable?.dataType;

      if (isVariableValueEmpty(value)) return false;
      if (isContextReference(value)) return false;

      switch (dataType) {
         case "number":
            return !Number.isFinite(Number(value));
         case "boolean":
            return ![true, false, "true", "false"].includes(value);
         case "object":
            try {
               const parsed = JSON.parse(value);
               return parsed === null || Array.isArray(parsed) || typeof parsed !== "object";
            } catch {
               return true;
            }
         case "array":
            try {
               const parsed = JSON.parse(value);
               return !Array.isArray(parsed);
            } catch {
               return true;
            }
         default:
            return false;
      }
   };

   const isTypedDefaultValueInvalid = (variable) => {
      const value = getVariableDefaultValue(variable);

      if (isVariableValueEmpty(value)) return false;

      return isTypedVariableValueInvalid(variable, value);
   };

   const getTypedDefaultValue = (variable) => {
      const value = getVariableDefaultValue(variable);

      if (isVariableValueEmpty(value)) return value ?? "";

      switch (variable?.dataType) {
         case "number":
            return Number(value);
         case "boolean":
            return value === true || value === "true";
         case "object":
            try {
               return JSON.parse(value);
            } catch {
               return value;
            }
         case "array":
            try {
               return JSON.parse(value);
            } catch {
               return value;
            }
         default:
            return value;
      }
   };

   const getResolvedVariableValue = (variable) => {
      const rawValue = getVariableDefaultValue(variable);
      const contextValue = typeof rawValue === "string" && rawValue.startsWith("@") ? getContextValue(rawValue, context) : undefined;
      return contextValue ?? getTypedDefaultValue(variable);
   };

   const updateStepDataContext = (element) => {
      const node = getNode(element.elementId);
      let dataForContext = element.data;

      if (node.data.type === COMMON_COMPONENT_TYPES.VARIABLES) {
         dataForContext = {};

         for (const key in element.data) {
            
            if ((!key.startsWith("var_") && !key.startsWith("reference_var_")) || !element.data[key]?.value) {
               continue;
            }

            dataForContext = {
               ...dataForContext,
               [element.data[key].value]: key.startsWith("reference_var_") ? null : getResolvedVariableValue(element.data[key])
            };
         }
      }

      if (node.data.type === COMMON_COMPONENT_TYPES.SESSION_STORAGE) {
         const action = element.data?.action?.value;
         if (action === "retrieve" || action === "createIfNew") {
            dataForContext = {
               ...dataForContext,
               session: null
            };
         }
      }

      if (node.data.type === COMMON_COMPONENT_TYPES.REST_API && element.data.payloadContentType?.value === "json" && element.data.payload) {
         dataForContext = {
            ...dataForContext,
            payload: {
               value: replaceStringContextValue(element.data.payload.value, context)
            }
         };
      }
   };

   const updateCanvasLabelNode = (element, flowNodes) => {
      const tabNode = model.getNodeById(tabId);
      const labelName = actionForm.labelName?.value;

      if (tabNode) {
         model.doAction(Actions.updateNodeAttributes(tabId, { name: labelName }));
      }

      flowNodes.forEach((node) => {
         // we need to update the parent branch's branches array so that it wont contain any stale updated label
         // that causes updated label not being highlighted when logs appear
         if (node.id === selectedNode.data.source && node.data.type === COMMON_COMPONENT_TYPES.SELECTION) {
            const updatedBranches = node.data.flowElement.data.branches.map((branchNode) =>
               branchNode.id === selectedNode.elementId
                  ? {
                       ...branchNode,
                       label: labelName
                    }
                  : branchNode
            );
            node.data.flowElement.data.branches = updatedBranches;
         }

         if (node.id === selectedNode?.elementId) {
            node.data = {
               ...node.data,
               flowElement: element,
               label: labelName
            };
         }

      });
   };

   const checkExecutionReportOptionSelection = () => {
      const selected = Object.keys(actionForm).find((k) => actionForm[k]?.value);
      if (!selected) {
         toast.warning("Please select at least one option for how you would like to get the results.");
      }

      return selected;
   };

   const encodePasswords = async () => {
      for (const fieldValue of Object.values(actionForm)) {
         if (fieldValue.isPassword && !fieldValue.isEncoded) {
            fieldValue.value = await encode(fieldValue.value);
            fieldValue.isEncoded = true;
         }
      }
      return;
   };

   const checkAndUpdateFlowVariables = () => {
      const node = getNode(selectedNode.elementId);
      const flowName = currentSavedFlow?.name;

      if (node && Object.keys(actionForm).length && node.data.type === COMMON_COMPONENT_TYPES.VARIABLES) {
         const varsData = [];
         for (const key in actionForm) {
            if (!key.startsWith("var_") && !key.startsWith("reference_var_")) {
               continue;
            }

            varsData.push({
               variableName: actionForm[key].value,
               variableValue: key.startsWith("reference_var_") ? null : getResolvedVariableValue(actionForm[key])
            });
         }

         dispatch(updateContextVariable(node.data?.componentKey, varsData, flowName));
      }
   };

   const getVariablesForCurrentStep = (elementId, withJsonOnly) => {
      let variables = [];

      try {
         const nodes = getNodes();
         const edges = getEdges();
         const trgNode = getNode(elementId);
         const incomers = getIncomers(trgNode, nodes, edges);
         const orderedSteps = [];

         const startPoint = nodes.filter((n) => {
            return edges.find((e) => e.source === n.id) && !edges.find((e) => e.target === n.id);
         });
         const getChildNodes = (curNode) => {
            const step = nodes.find((s) => s.id === curNode.id);
            if (step) {
               orderedSteps.push(step);
            }

            let outG = getOutgoers(curNode, nodes, edges);
            if (outG.length) {
               if (outG.length === 1) {
                  getChildNodes(outG[0]);
               } else {
                  outG.forEach((o) => {
                     getChildNodes(o);
                  });
               }
            }
         };

         if (startPoint.length === 1) {
            getChildNodes(startPoint[0]);
         }

         if (incomers.length) {
            let lastStepForVariables = false;

            for (const s of orderedSteps) {
               const stepOutgoers = getOutgoers(s, nodes, edges);
               let vars;

               if (s.data.type === COMMON_COMPONENT_TYPES.VARIABLES && !withJsonOnly) {
                  vars = s.data.flowElement.data;

                  for (const key in vars) {
                     if (vars[key].value) {
                        variables.push({ name: vars[key].value });
                     }
                  }
               } else {
                  vars = s.data?.flowElement?.variables;
                  if (vars) {
                     if (withJsonOnly) {
                        vars = vars.filter((v) => v.defaultvalue);
                        setVariableWithJson(vars);
                     }
                     variables = [...variables, ...vars];
                     //remove duplicates
                     variables = removeDuplicateObjects(variables, "name");
                  }
               }

               if (lastStepForVariables) {
                  break;
               }

               if (stepOutgoers.length && stepOutgoers[0].id === elementId) {
                  lastStepForVariables = true;
               }
            }
         }

         sortString(variables, "name");
      } catch (e) {
         console.log(e);
      }
      return variables;
   };

   const setVariableActions = (action) => {
      const newDataInsideAction = [];
      const variableData = flowElement?.data ?? {};
      for (const key in variableData) {
         if (key.startsWith("var_")) {
            const savedVariable = variableData[key] || {};
            newDataInsideAction.push({
               field: key,
               label: "Name",
               type: "string",
               dataType: savedVariable.dataType,
               isRequired: true,
               placeholder: "",
               secondaryInput: {
                  field: `${key}value`,
                  value: "",
                  label: "Default value",
                  placeholder: "",
                  isRequired: false
               }
            });
         }
      }

      newDataInsideAction.push({ field: "addVariable", label: "Add New", type: "button" });
      for (const key in variableData) {
         if (key.startsWith("reference_var_")) {
            newDataInsideAction.push({ field: key, label: "", type: "string", isRequired: true, placeholder: "" });
         }
      }
      newDataInsideAction.push({ field: "addOutputReference", label: "Add New", type: "button" });
      action.dataInsideAction = newDataInsideAction;
      return action;
   };

   const setStringActions = (action) => {
      const newAction = { ...action };
      const newDataInsideAction = action.dataInsideAction.filter((insideAction) => !insideAction.field.includes("field_") && !insideAction.field.includes("addField"));

      if (action.actionName === "HSet") {
         if (Object.keys(flowElement).length === 0) return action;
         for (const key in flowElement.data) {
            if (key.includes("field")) {
               newDataInsideAction.push({
                  field: key,
                  label: `Field ${key[key.length - 1]}`,
                  type: "string",
                  isRequired: true,
                  placeholder: "",
                  secondaryInput: {
                     field: `fValue_${key[key.length - 1]}`,
                     value: "",
                     label: "Default value " + key[key.length - 1],
                     placeholder: "",
                     isRequired: true
                  }
               });
            }
         }

         if (newDataInsideAction.length) {
            newDataInsideAction.push({
               field: "addField",
               label: "Add New",
               type: "button"
            });
            newAction.dataInsideAction = newDataInsideAction;
         }

         newAction.dataInsideAction = newDataInsideAction;
      }

      return newAction;
   };

   const removeEmptyValues = (obj = {}) => {
      const keys = Object.keys(obj);
      keys.forEach((key) => {
         const field = obj[key];
         if ((typeof field === "object" && !Array.isArray(field) && (field.value === undefined || field.value === null)) || (typeof field === "string" && !field)) {
            delete obj[key];
            return;
         }
      });
   };

   const setExecutionReportActions = () => {
      const { actions } = selectedNode;
      let options = {};

      if (Array.isArray(actions) && actions.length > 0) {
         actions[0].dataInsideAction?.forEach((d) => {
            options[d.field] = { values: d.value, isPassword: false };
         });
      }

      setActionForm(options);
   };

   const setRestAndWebhookComponentActions = (action) => {
      let targetAction = action || actions[0];
      let data = flowElement.data;

      // Sets the authentication fields based on the selected authentication type
      if (data) {
         targetAction.dataInsideAction = targetAction.dataInsideAction?.map((act) => {
            if (act.field === "authenticationType" && data?.authenticationType?.value) {
               return {
                  ...act,
                  listItems: [...act.listItems.slice(0, 1), ...restApiFields[data?.authenticationType?.value]]
               };
            }

            if (act.field === "payloadContentType" && data?.formData?.value) {
               return {
                  ...act,
                  listItems: [...act.listItems.slice(0, 1), ...payloadContentTypeFields[data?.payloadContentType?.value]]
               };
            }
            return act;
         });
      }

      targetAction.dataInsideAction = targetAction.dataInsideAction?.map((item) =>
         item.field === "endpoint"
            ? {
                 ...item,
                 isRequired: true
              }
            : item
      );

      return targetAction;
   };

   const setSftpComponentActions = (action) => {
      let targetAction = action || actions[0];
      let data = flowElement.data;
      if (data && data?.operation?.value !== "") {
         const operationFieldIndex = targetAction.dataInsideAction.findIndex((action) => action.field === "operation");

         targetAction.dataInsideAction = [...(targetAction.dataInsideAction?.slice(0, operationFieldIndex + 1) ?? []), ...(Array.isArray(sftpFields[data?.operation?.value]) ? sftpFields[data?.operation?.value] : [])];
      }

      return targetAction;
   };

   const setCustomComponentActions = () => {
      if (selectedNode?.customFunctions && selectedNode.customFunctions.length) {
         let customJsAction = selectedNode.actions.find((a) => a.actionName === "ExecuteJS");
         const addOptions = (fieldName) => {
            const functionNameField = customJsAction.dataInsideAction?.find((d) => d.field === "functionName");

            if (functionNameField) {
               functionNameField.options = [];

               selectedNode[fieldName].forEach((f) => {
                  functionNameField.options.push({ value: f, text: f });
               });
            }
         };

         if (customJsAction) {
            addOptions("customFunctions");
         }

         customJsAction = selectedNode.actions.find((a) => a.actionName === "ExecuteAPI");

         if (customJsAction) {
            addOptions("customAPIs");
         }
      }
   };

   const getInvokeFlowOptions = ({ isInvokeSubFlowOptions }) => {
      let invokeFlowOptions = [];

      const currentFlowIntegrations = currentSavedFlow.integrations ? JSON.parse(currentSavedFlow.integrations) : [];

      // only check for avaible flow if the current flow has integration
      if (currentFlowIntegrations.length) {
         flows.forEach((flow) => {
            if (flow.id === currentSavedFlow.id || flow.trashed) return;

            const flowIntegrations = flow.integrations ? JSON.parse(flow.integrations) : [];

            if (!flowIntegrations.length) return;

            flowIntegrations.forEach((flowIntegration) => {
               if (!invokeFlowOptions.find((option) => option.value === flow.id) && currentFlowIntegrations.includes(flowIntegration)) {
                  const option = { value: flow.id, text: flow.name };
                  if (isInvokeSubFlowOptions) {
                     if (flow.logo) {
                        option.logoUrl = flow.logo;
                     } else {
                        option.logoComponent = FlowDefaultIcon;
                     }
                  }
                  invokeFlowOptions.push(option);
               }
            });
         });
      }

      return invokeFlowOptions;
   };

   const setGoToComponentActions = (action) => {
      const targetAction = action || actions[0];
      let goToNodeOptions = [];

      // set the goto flow options based on the current saved flow for now. later can target other flow
      if (currentSavedFlow) {
         let flowSteps = getNodes();

         goToNodeOptions = flowSteps
            ?.filter((node) => node.type !== NODE_TYPES.LABEL && node.type !== NODE_TYPES.HIDDEN && node.id !== selectedNode?.elementId && !node.data?.isBranch)
            .map((node) => {
               return {
                  value: node.id,
                  text: `${node.data?.label} ${node.data?.componentKey ? `(${node.data.componentKey})` : ""}`
               };
            })
            .sortAlphabetical("text");
      }

      if (Array.isArray(targetAction.dataInsideAction) && targetAction.dataInsideAction.length > 0) {
         targetAction.dataInsideAction[0].options = goToNodeOptions;
      }

      return targetAction;
   };

   const checkIsDirty = () => {
      if (actionForm && initialActionForm) {
         const actionFormClone = { ...actionForm };
         const initialActionFormClone = { ...initialActionForm };
         const fieldsToIgnore = [];

         componentAction?.dataInsideAction?.forEach((action) => {
            if (action.type === "list") {
               action?.listItems?.forEach((li) => {
                  if (li.type === ACTION_TYPES.READ_ONLY) {
                     fieldsToIgnore.push(li.field);
                  }
               });
            } else if (action.type === ACTION_TYPES.READ_ONLY) {
               fieldsToIgnore.push(action.field);
            }
         });

         if (selectedNode.key === COMMON_COMPONENT_TYPES.CODE) {
            delete initialActionFormClone.code?.errors;
            delete actionFormClone.code?.errors;
         }

         const isDirty = !deepEqual(initialActionFormClone, actionFormClone, fieldsToIgnore);

         onDirtyChange?.(isDirty);
      }
   };

   useEffect(() => {
      switch (selectedNode?.key) {
         case COMMON_COMPONENT_TYPES.SESSION_STORAGE:
            tryCatchWrapper(setSessionStorageActions)(componentAction);
            break;
         default:
            setCustomComponentActions();
            break;
      }
   }, [componentAction, actionForm?.action?.value]);

   useEffect(() => {
      if (hasMounted.current || !selectedNode || !flowElement) return;

      hasMounted.current = true;

      if (showComponentPropsPanel) {
         setActionForm(null);
         if (flowElement?.action) {
            const act = selectedNode?.actions?.find((a) => a.actionName === flowElement?.action);

            if (act) {
               tryCatchWrapper(onListItemClick)(act);
            }
         } else if (selectedNode?.actions && selectedNode?.actions?.length === 1) {
            setTimeout(() => {
               setShouldManuallySave(true);
            }, 1750);
            tryCatchWrapper(onListItemClick)(selectedNode?.actions[0]);
         } else {
            setShowMainList(true);
         }
      } else {
         setShowMainList(true);
      }
   }, [flowElement, flows, showComponentPropsPanel]);

   // this effect is for making the component properties buttons to only show if specicif inputs are filled
   useEffect(() => {
      if (!actionForm) return;

      checkIsDirty();
   }, [actionForm]);

   useImperativeHandle(ref, () => ({
      triggerCodeEditorTestButton: () => {
         if (isFunction(codeEditorInputRef?.current?.triggerCodeEditorTestButton)) {
            codeEditorInputRef.current.triggerCodeEditorTestButton();
         }
      },
      triggerDatabaseConnectionTest: () => {
         if (isFunction(sqlEditorInputRef?.current?.triggerTestSqlConnection)) {
            sqlEditorInputRef.current.triggerTestSqlConnection();
         }
      },
      triggerDatabaseQueryTest: () => {
         if (isFunction(sqlEditorInputRef?.current?.triggerTestSqlQuery)) {
            sqlEditorInputRef.current.triggerTestSqlQuery();
         }
      },
      reset: () => {
         selectionRef.current?.revertBranches?.();
      }
   }));

   useEffect(() => {
      if (selectedNode?.elementId) {
         const nodeExists = getNode(selectedNode.elementId);

         if (!nodeExists) {
            dispatch(hideComponentPropsPanel({ tabId }));
         }
      }
   }, [selectedNode, getNode, dispatch]);

   useEffect(() => {
      if (!actionForm) return;

      if (!showComponentPropsPanel) {
         setActionForm(null);
      }

      dispatch({
         type: SET_CURRENT_ACTION_FORM,
         payload: actionForm
      });
   }, [showComponentPropsPanel, actionForm]);

   useEffect(() => {
      if (currentActionForm) {
         setInitialandActualActionForm(currentActionForm);
      }

      return () => {
         dispatch({
            type: SET_CURRENT_ACTION_FORM,
            payload: null
         });
         setActionForm(null);
         setInitialActionForm(null)
      };
   }, []);

   useEffect(() => {
      if (!selectedNode) return;

      if (shouldManuallySave && selectedNode.connectorId && Object.keys(selectedNode.data.flowElement).length === 0) {
         submitActions();
         setShouldManuallySave(false);
      }
   }, [shouldManuallySave, selectedNode]);

   return (
      <ActionsWrapper className="component-props-panel">
          {showMainList ? (
             <List sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>{generateActions()}</List>
          ) : (
            <>
               <ComponentPropsContext.Provider
                  value={{
                     actionForm,
                     componentAction,
                     formErrors,
                     variableWithJson
                  }}
               >
                  <ComponentPropsContextApi.Provider
                     value={{
                        setActionForm,
                        setComponentAction,
                        setFormErrors,
                        setShowMainList,
                        getVariablesForCurrentStep,
                        filterValidationErrors,
                        setInitialActionForm
                     }}
                  >
                     <SoftBox sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                        <form id="action-form" onSubmit={submitActions}>
                           <FormBody codeEditorInputRef={codeEditorInputRef} sqlEditorInputRef={sqlEditorInputRef} actionFieldsRef={actionFieldsRef} tabId={tabId} selectionRef={selectionRef} />
                        </form>
                     </SoftBox>

                     {(!isFlowActive || ((!isCommonComponent && showComponentPropsPanel) || isMultipleActions)) && (
                        <SoftBox
                           className={`btns-action ${(isCommonComponent && !isMultipleActions) || !showComponentPropsPanel ? "single-btn" : ""}`}
                           sx={{
                              flexShrink: 0,
                              zIndex: 1000,
                              display: "flex",
                              gap: 2,
                              padding: "8px 0 8px 0",
                              backgroundColor: isDarkTheme ? THEME_COLORS.DARK.SECONDARY_BG : THEME_COLORS.LIGHT.SECONDARY_BG,
                              "& button": {
                                 flex: 1,
                                 maxWidth: "100px"
                              },
                              justifyContent: "flex-end"
                           }}
                        >
                           {((!isCommonComponent && showComponentPropsPanel) || isMultipleActions) && (
                              <SoftButton component={Button} color="primary" variant="outlined" sx={{ minWidth: "90px" }} onClick={() => setShowMainList(true)}>
                                 {t("buttons.back")}
                              </SoftButton>
                           )}
                           {!isFlowActive && (
                              <>
                                 <SoftButton variant="outlined" color="info" sx={{ minWidth: "90px" }} onClick={() => onDirtyChange?.("cancel")} type="button">
                                    Cancel
                                 </SoftButton>
                                 <SoftButton type="button" form="action-form" color="primary" disabled={!isDirty || !allowEditWorkflow(isReadOnlyAccess)} sx={{ minWidth: "90px" }} onClick={submitActions}>
                                    Save
                                 </SoftButton>
                              </>
                           )}
                        </SoftBox>
                     )}
                  </ComponentPropsContextApi.Provider>
               </ComponentPropsContext.Provider>
            </>
         )}
      </ActionsWrapper>
   );
};

export default memo(ComponentActionsGenerator);
